import { supabase } from '../../supabase';
import { stampCreatedAt } from '../../utils/dbTimestamps';
import { ensureGymId, getCachedGymId } from '../receptionist/gymContext';
import { rowToPackage } from '../receptionist/mappers';
import { cachedQuery, invalidateCache } from '../cache/queryCache';
import { runGenericMutation } from '../offline/runMutation';

const SUPABASE_PAGE_SIZE = 1000;

async function fetchAllPaged(buildQuery) {
  const rows = [];
  let from = 0;
  for (;;) {
    const to = from + SUPABASE_PAGE_SIZE - 1;
    const { data, error } = await buildQuery().range(from, to);
    if (error) throw error;
    const chunk = data || [];
    rows.push(...chunk);
    if (chunk.length < SUPABASE_PAGE_SIZE) break;
    from += SUPABASE_PAGE_SIZE;
  }
  return rows;
}

async function gymId() {
  const cached = getCachedGymId();
  if (cached) return cached;
  return ensureGymId();
}

async function bustGymCache() {
  invalidateCache(`gym:${await gymId()}:`);
}

function getMissingColumn(message = '') {
  return message.match(/Could not find the '([^']+)' column/i)?.[1]
    || message.match(/column "([^"]+)" of relation/i)?.[1]
    || message.match(/column \w+\.([^ ]+) does not exist/i)?.[1]
    || null;
}

function rowToBranch(row, memberCount = 0) {
  return {
    id: row.id,
    name: row.name,
    location: row.location || '',
    address: row.address || '',
    manager: row.manager || '',
    phone: row.phone || '',
    status: row.status || 'active',
    createdAt: row.created_at || row.createdAt || '',
    members: Number(memberCount ?? row.members) || 0,
    performance: { revenue: 0, attendance: 0, reports: 0 },
  };
}

function isBranchRpcMissing(error) {
  const msg = String(error?.message || error || '').toLowerCase();
  return (
    msg.includes('could not find the function')
    || msg.includes('function public.get_branch_overview')
    || msg.includes('schema cache')
    || (msg.includes('get_branch_overview') && msg.includes('does not exist'))
  );
}

function mapBranchOverview(data) {
  const rows = Array.isArray(data?.branches) ? data.branches : [];
  return rows.map((row) => rowToBranch(row, row.members));
}

async function fetchBranchOverviewRpc(gid, startDate = '', endDate = '') {
  const from = String(startDate || '').slice(0, 10);
  const to = String(endDate || '').slice(0, 10);
  const { data, error } = await supabase.rpc('get_branch_overview', {
    p_gym_id: gid,
    p_date_from: /^\d{4}-\d{2}-\d{2}$/.test(from) ? from : null,
    p_date_to: /^\d{4}-\d{2}-\d{2}$/.test(to) ? to : null,
  });
  if (error) {
    if (isBranchRpcMissing(error)) return null;
    throw error;
  }
  if (!data || data.error) return null;
  return mapBranchOverview(data);
}

/** Count gym members keyed by lowercase branch name (members.branch or profile.branch). */
async function countMembersByBranchName(gid) {
  const counts = new Map();
  let data = [];
  try {
    data = await fetchAllPaged(() => (
      supabase
        .from('members')
        .select('id, branch, profile')
        .eq('gym_id', gid)
        .order('id', { ascending: true })
    ));
  } catch (error) {
    // Older schemas without branch column — try profile only via *
    try {
      data = await fetchAllPaged(() => (
        supabase
          .from('members')
          .select('id, profile')
          .eq('gym_id', gid)
          .order('id', { ascending: true })
      ));
    } catch {
      return counts;
    }
  }

  data.forEach((row) => {
    const name = String(row.branch || row.profile?.branch || '').trim();
    if (!name) return;
    const key = name.toLowerCase();
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return counts;
}

function withMemberCounts(branches, counts) {
  return (branches || []).map((row) => {
    const key = String(row.name || '').trim().toLowerCase();
    return rowToBranch(row, counts.get(key) || 0);
  });
}

function formToBranchRow(form, gid) {
  return {
    gym_id: gid,
    name: form.name.trim(),
    location: form.location?.trim() || null,
    address: form.address?.trim() || null,
    manager: form.manager?.trim() || null,
    phone: form.phone?.trim() || null,
    status: form.status || 'active',
  };
}

function durationToUnit(duration) {
  const map = {
    Weekly: 'week',
    Monthly: 'month',
    Quarterly: 'quarter',
    'Half-Yearly': 'half_year',
    Yearly: 'year',
    Lifetime: 'lifetime',
  };
  return map[duration] || 'month';
}

function formToPackageRow(form, gid) {
  const serialStart = Number(form.serialNo ?? form.serialNoStart);
  return {
    gym_id: gid,
    name: form.name.trim(),
    duration: form.duration,
    duration_unit: durationToUnit(form.duration),
    price: Number(form.price) || 0,
    registration_fee: Number(form.registrationFee) || 0,
    tax: Number(form.tax) || 0,
    benefits: form.benefits || '',
    status: form.status || 'active',
    branch_id: form.branchId || null,
    branch_name: form.branch || null,
    freeze_option: Boolean(form.freezeOption),
    auto_renewal: Boolean(form.autoRenewal),
    serial_no_start: Number.isFinite(serialStart) && serialStart > 0 ? Math.floor(serialStart) : null,
  };
}

// ─── Branches ──────────────────────────────────────────────

export async function fetchBranches(range = {}) {
  const gid = await gymId();
  const startDate = String(range.startDate || '').slice(0, 10);
  const endDate = String(range.endDate || '').slice(0, 10);
  const cacheKey = `gym:${gid}:branches:rpc-v1:${startDate || 'all'}:${endDate || 'all'}`;

  return cachedQuery(cacheKey, async () => {
    try {
      const rpc = await fetchBranchOverviewRpc(gid, startDate, endDate);
      if (rpc) return rpc;
    } catch (err) {
      console.warn('Branch overview RPC failed, using fallback', err?.message || err);
    }
    return fetchBranchesLegacy(gid);
  });
}

async function fetchBranchesLegacy(gid) {
  let { data, error } = await supabase
    .from('branches')
    .select('*')
    .eq('gym_id', gid)
    .order('name', { ascending: true });

  if (error) {
    if (/branches.*schema cache|relation.*branches.*does not exist/i.test(error.message)) {
      throw new Error(
        'Branches table not found. Open Supabase → SQL Editor, run supabase/create_branches_table.sql, then refresh this page.',
      );
    }
    throw error;
  }

  const counts = await countMembersByBranchName(gid);
  return withMemberCounts(data || [], counts);
}

export async function fetchBranchById(id) {
  const { data, error } = await supabase.from('branches').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const gid = data.gym_id || await gymId();
  try {
    const rpc = await fetchBranchOverviewRpc(gid);
    const match = (rpc || []).find((row) => String(row.id) === String(data.id));
    if (match) return { ...match, ...rowToBranch(data, match.members) };
  } catch {
    // fall through
  }
  const counts = await countMembersByBranchName(gid);
  const key = String(data.name || '').trim().toLowerCase();
  return rowToBranch(data, counts.get(key) || 0);
}

export async function fetchBranchNames() {
  const branches = await fetchBranches();
  return branches.filter((b) => b.status === 'active').map((b) => b.name);
}

/** Names only — no member-count scan (for filters / warm / finance). */
export async function fetchBranchNamesLite() {
  const gid = await gymId();
  return cachedQuery(`gym:${gid}:branch-names`, async () => {
    const { data, error } = await supabase
      .from('branches')
      .select('name, status')
      .eq('gym_id', gid)
      .order('name', { ascending: true });

    if (error) {
      if (/branches.*schema cache|relation.*branches.*does not exist/i.test(error.message)) {
        return [];
      }
      throw error;
    }

    return (data || [])
      .filter((b) => (b.status || 'active') === 'active')
      .map((b) => b.name)
      .filter(Boolean);
  });
}

export async function saveBranch(form, id) {
  const gid = await gymId();
  const row = formToBranchRow(form, gid);
  if (id) {
    const { data, error } = await runGenericMutation({
      table: 'branches',
      op: 'update',
      payload: row,
      match: { id },
      source: 'catalog',
    });
    if (error) throw error;
    await bustGymCache();
    try {
      const rpc = await fetchBranchOverviewRpc(gid);
      const match = (rpc || []).find((row) => String(row.id) === String(data.id));
      if (match) return { ...match, ...rowToBranch(data, match.members) };
    } catch {
      // new/renamed branch still returns without a full member scan
    }
    return rowToBranch(data, 0);
  }
  const { data, error } = await runGenericMutation({
    table: 'branches',
    op: 'insert',
    payload: stampCreatedAt(row),
    source: 'catalog',
  });
  if (error) throw error;
  await bustGymCache();
  return rowToBranch(data, 0);
}

export async function deleteBranch(id) {
  const { error } = await runGenericMutation({
    table: 'branches',
    op: 'delete',
    match: { id },
    source: 'catalog',
  });
  if (error) throw error;
  await bustGymCache();
}

// ─── Packages ──────────────────────────────────────────────

/** Unique active members per package name (from memberships, not stale packages.members). */
async function countMembersByPackageName(gid) {
  const counts = new Map();
  let data = [];
  try {
    data = await fetchAllPaged(() => (
      supabase
        .from('memberships')
        .select('member_id, package_name, status, id')
        .eq('gym_id', gid)
        .order('id', { ascending: true })
    ));
  } catch {
    return counts;
  }

  const byPkg = new Map();
  data.forEach((row) => {
    const status = String(row.status || '').toLowerCase();
    if (status === 'cancelled' || status === 'expired') return;
    const pkg = String(row.package_name || '').trim();
    if (!pkg || !row.member_id) return;
    const key = pkg.toLowerCase();
    if (!byPkg.has(key)) byPkg.set(key, new Set());
    byPkg.get(key).add(row.member_id);
  });

  byPkg.forEach((set, key) => counts.set(key, set.size));
  return counts;
}

export async function fetchAllPackages({ branch, status, includeMemberCounts = true } = {}) {
  const gid = await gymId();
  const cacheKey = `gym:${gid}:packages:v4:${branch || 'all'}:${status || 'all'}:c${includeMemberCounts ? 1 : 0}`;
  return cachedQuery(cacheKey, async () => {
    let query = supabase.from('packages').select('*').eq('gym_id', gid).order('name', { ascending: true });
    if (status) query = query.eq('status', status);

    // Exact branch filter only when requested; null/empty branch packages are included below.
    let { data, error } = await query;
    if (error) throw error;

    let rows = data || [];
    if (branch && branch !== 'all') {
      const needle = String(branch).trim().toLowerCase();
      const matched = rows.filter((row) => {
        const name = String(row.branch_name || '').trim().toLowerCase();
        return !name || name === needle;
      });
      // If nothing matches this branch (including unassigned), show all active gym packages.
      rows = matched.length ? matched : rows;
    }

    // Member counts require scanning all memberships — skip for sale/subscription UIs.
    const counts = includeMemberCounts ? await countMembersByPackageName(gid) : new Map();
    return rows.map((row) => {
      const key = String(row.name || '').trim().toLowerCase();
      return rowToPackage(row, counts.get(key) || 0);
    });
  });
}

export async function fetchPackageById(id) {
  const { data, error } = await supabase.from('packages').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const gid = data.gym_id || await gymId();
  const counts = await countMembersByPackageName(gid);
  const key = String(data.name || '').trim().toLowerCase();
  return rowToPackage(data, counts.get(key) || 0);
}

export async function savePackage(form, id) {
  const gid = await gymId();
  const row = formToPackageRow(form, gid);
  // Never persist a fake members counter — live count comes from memberships
  delete row.members;
  const wantedSerial = row.serial_no_start;

  const persist = async (rawPayload) => {
    if (id) {
      return runGenericMutation({
        table: 'packages',
        op: 'update',
        payload: rawPayload,
        match: { id },
        source: 'catalog',
      });
    }
    return runGenericMutation({
      table: 'packages',
      op: 'insert',
      payload: stampCreatedAt(rawPayload),
      source: 'catalog',
    });
  };

  // Drop unknown optional columns (except we re-apply serial after)
  const payload = { ...row };
  let data = null;
  let error = null;
  for (let attempt = 0; attempt < 20; attempt += 1) {
    ({ data, error } = await persist(payload));
    if (!error) break;
    const missing = getMissingColumn(error.message || '');
    if (missing && Object.prototype.hasOwnProperty.call(payload, missing)) {
      delete payload[missing];
      continue;
    }
    // .single()-style empty select after successful write
    if (/multiple \(or no\) rows returned|Results contain 0 rows|JSON object requested/i.test(error.message || '')) {
      error = null;
      break;
    }
    break;
  }
  if (error) throw error;

  let packageId = id || data?.id;
  if (!packageId && form.name) {
    const { data: found } = await supabase
      .from('packages')
      .select('*')
      .eq('gym_id', gid)
      .eq('name', form.name.trim())
      .order('created_at', { ascending: false })
      .limit(1);
    data = found?.[0] || data;
    packageId = data?.id || packageId;
  }

  // Always write serial in a dedicated step so it is not lost when other columns fail
  if (wantedSerial != null && packageId) {
    const { error: serialError } = await supabase
      .from('packages')
      .update({ serial_no_start: wantedSerial })
      .eq('id', packageId);
    if (serialError && /serial_no_start/i.test(serialError.message || '')) {
      throw new Error(
        'Serial No column missing on packages. Run supabase/fix_package_membership_serial.sql in Supabase SQL Editor, then try again.',
      );
    }
    if (serialError) throw serialError;
  }

  // Re-fetch so Serial No column always reflects DB
  if (packageId) {
    const { data: fresh } = await supabase
      .from('packages')
      .select('*')
      .eq('id', packageId)
      .maybeSingle();
    if (fresh) {
      data = {
        ...fresh,
        serial_no_start: fresh.serial_no_start ?? wantedSerial ?? fresh.serial_no_start,
      };
    } else if (wantedSerial != null) {
      data = { ...(data || {}), id: packageId, serial_no_start: wantedSerial };
    }
  }

  if (!data?.id && !packageId) {
    throw new Error('Package was not saved. Please try again.');
  }

  await bustGymCache();
  const counts = await countMembersByPackageName(gid);
  const key = String((data || row).name || '').trim().toLowerCase();
  return rowToPackage(
    { ...(data || row), id: packageId || data?.id, serial_no_start: data?.serial_no_start ?? wantedSerial },
    id ? (counts.get(key) || 0) : 0,
  );
}

export async function deletePackage(id) {
  const { error } = await runGenericMutation({
    table: 'packages',
    op: 'delete',
    match: { id },
    source: 'catalog',
  });
  if (error) throw error;
  await bustGymCache();
}

export async function fetchActivePackagesForBranch(branchName) {
  // Sale UIs only need package names/prices — skip membership count scan.
  return fetchAllPackages({
    branch: branchName || 'all',
    status: 'active',
    includeMemberCounts: false,
  });
}
